import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";

const FAQ = [
  {
    question: "What is a solar fleet dashboard?",
    answer:
      "A solar fleet dashboard is a single interface where installers see status, risk, and health across all residential solar and battery systems they manage. Instead of logging into each OEM portal, you get one view: how many systems are live, how many are at overnight reserve risk, and which need attention.",
  },
  {
    question: "Can I filter by brand or grid mode?",
    answer:
      "Yes. Watt Tower’s solar fleet dashboard lets you filter by provider (Sol-Ark, EG4, SolarEdge), by tag, and by grid mode. You can focus on off-grid systems, hybrid systems, or a specific customer segment without switching apps.",
  },
  {
    question: "How do I see overnight risk across the fleet?",
    answer:
      "The dashboard shows fleet-level “At Risk” counts for overnight reserve. You see how many systems may run low by sunrise. Drill into any property for projected kWh at sunrise and confidence level. Alerts can be routed to you, the customer, or both.",
  },
  {
    question: "Does it work with multiple inverter brands?",
    answer:
      "Yes. Watt Tower supports Sol-Ark, EG4, and SolarEdge. Multi-vendor telemetry is unified in one solar fleet dashboard so you do not need separate logins per brand.",
  },
];

export default function SolarFleetDashboard() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <PublicLayout>
      <Helmet>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      </Helmet>
      <article className="mx-auto max-w-3xl px-4 py-16 lg:px-8">
        <h1 className="mb-6 font-sans text-3xl font-bold md:text-4xl">
          Solar Fleet Dashboard
        </h1>
        <p className="mb-10 font-sans text-lg leading-relaxed text-wt-text-secondary">
          One view across every residential solar and battery system you
          manage. Multi-site monitoring, overnight reserve risk, and
          battery health in a single installer dashboard.
        </p>

        <section className="space-y-10">
          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Why Installers Need a Fleet Dashboard
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Managing many solar and battery systems means juggling multiple
            OEM apps unless you have a solar fleet dashboard. Watt Tower
            gives you one pane of glass: live counts, at-risk counts,
            offline counts, and the ability to drill into any property. You
            see system status in seconds and identify overnight reserve risk
            before the customer calls.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Multi-Site Visibility in One Place
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            The solar fleet dashboard summarizes all sites you manage.
            Status (Live, Stale, Offline), overnight risk flags, and battery
            health are visible at a glance. Filter by provider, tag, or grid
            mode. You no longer log into three separate portals to answer
            “how is the fleet doing?”
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Overnight Reserve Risk at Fleet Level
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            One of the main values of a solar fleet dashboard is seeing
            overnight reserve risk across the fleet. Watt Tower forecasts
            projected kWh at sunrise per system and flags “At Risk” at the
            fleet level. You can prioritize follow-up or notifications
            instead of reacting after a 2 a.m. call.
          </p>

          <h2 className="font-sans text-2xl font-semibold text-wt-text-primary">
            Built for Sol-Ark, EG4, and SolarEdge
          </h2>
          <p className="font-sans text-base leading-relaxed text-wt-text-secondary">
            Watt Tower’s solar fleet dashboard supports Sol-Ark, EG4, and
            SolarEdge. Multi-vendor telemetry is unified so you get one
            operational view regardless of inverter brand. Read-only
            access; credentials encrypted and revocable.
          </p>
        </section>

        <section className="mt-16 border-t border-wt-border-default pt-12">
          <h2 className="mb-8 font-sans text-2xl font-semibold text-wt-text-primary">
            Frequently Asked Questions
          </h2>
          <dl className="space-y-6">
            {FAQ.map((item) => (
              <div key={item.question}>
                <dt className="font-sans text-base font-semibold text-wt-text-primary">
                  {item.question}
                </dt>
                <dd className="mt-2 font-sans text-base leading-relaxed text-wt-text-secondary">
                  {item.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-12 flex flex-wrap gap-4">
          <a href="/#installer-access">
            <Button className="h-11 bg-primary px-6 font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted">
              Request Installer Access
            </Button>
          </a>
          <Link to="/">
            <Button
              variant="outline"
              className="h-11 border-wt-border-default bg-transparent font-sans text-wt-text-primary hover:bg-white/5"
            >
              Back to home
            </Button>
          </Link>
        </div>
        <p className="mt-6 font-sans text-sm text-wt-text-muted">
          See also:{" "}
          <Link
            to="/solar-monitoring-software-for-installers"
            className="text-primary hover:underline"
          >
            solar monitoring software for installers
          </Link>
          ,{" "}
          <Link
            to="/battery-backup-monitoring"
            className="text-primary hover:underline"
          >
            battery backup monitoring
          </Link>
          ,{" "}
          <Link
            to="/solar-forecasting-software"
            className="text-primary hover:underline"
          >
            solar forecasting software
          </Link>
          .
        </p>
      </article>
    </PublicLayout>
  );
}
